import { AztecAddress } from "@aztec/aztec.js";
import {
  Avatar,
  Button,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Snippet,
  type SortDescriptor,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon, PlusIcon, RefreshCwIcon } from "lucide-react";
import { type Key, useMemo, useState } from "react";
import * as R from "remeda";
import { toast } from "sonner";
import { isAztecTokenContract } from "../aztec-utils";
import { type TokenDetails, useBridgedTokens } from "../queries/tokenReg";
import { useRefetchTokenBalances, useTokenBalances } from "../queries/useTokenBalances";
import { addShieldingUserAddedTokens } from "../state/shieldingUserAddedTokens";
import { shortHex } from "../utils";
import { NewL2TokenButton } from "./dev/NewL2TokenButton";
import { BalanceDisplay, SimplePrivateBalance } from "./SimpleBalanceDisplay";

export function TokensTable() {
  const { data: bridgedTokens, isPending: tokensPending } = useBridgedTokens();
  const { balances, isLoading: balancesLoading } = useTokenBalances();
  const { refetchBothBalances } = useRefetchTokenBalances();

  const [sortDesc, setSortDesc] = useState<SortDescriptor | undefined>();
  const [refreshingTokens, setRefreshingTokens] = useState<Set<string>>(new Set());

  // Create a combined list with token details and balances
  const tokensWithBalances = useMemo(() => {
    if (!bridgedTokens) return [];

    return bridgedTokens
      .filter((token) => token.l2Address)
      .map((token) => {
        const balance = balances.get(token.l2Address!);
        return {
          tokenDetail: token,
          balance,
        };
      })
      .filter(
        (item) =>
          // Show tokens with non-zero public balances or while loading
          balancesLoading || (item.balance && item.balance.publicBalance !== 0n),
      );
  }, [bridgedTokens, balances, balancesLoading]);

  const sortedItems = useMemo(() => {
    if (!tokensWithBalances.length) return [];
    if (!sortDesc || !sortDesc.column) return tokensWithBalances;

    const sortDir = sortDesc.direction === "descending" ? "desc" : "asc";

    switch (sortDesc.column) {
      case "tokenAddress":
        return R.sortBy(tokensWithBalances, [(item) => item.tokenDetail.l2Address || "", sortDir]);
      case "tokenName":
        return R.sortBy(tokensWithBalances, [(item) => item.tokenDetail.tokenName ?? "", sortDir]);
      case "tokenSymbol":
        return R.sortBy(tokensWithBalances, [(item) => item.tokenDetail.tokenSymbol ?? "", sortDir]);
      case "public_balance":
        return R.sortBy(tokensWithBalances, [(item) => item.balance?.publicBalance ?? 0n, sortDir]);
      case "private_balance":
        return R.sortBy(tokensWithBalances, [(item) => item.balance?.privateBalance ?? 0n, sortDir]);
      default:
        return tokensWithBalances;
    }
  }, [sortDesc, tokensWithBalances]);

  const handleRefresh = async (tokenAddress: string) => {
    setRefreshingTokens((prev) => new Set(prev).add(tokenAddress));
    try {
      await refetchBothBalances(tokenAddress);
      toast.success("Balances refreshed");
    } catch (error) {
      console.error("Failed to refresh balances:", error);
      toast.error("Failed to refresh balances");
    } finally {
      setRefreshingTokens((prev) => {
        const next = new Set(prev);
        next.delete(tokenAddress);
        return next;
      });
    }
  };

  const renderCell = (
    item: { tokenDetail: TokenDetails; balance?: { publicBalance: bigint; privateBalance?: bigint } },
    columnKey: Key,
  ) => {
    switch (columnKey) {
      case "tokenName":
        return (
          <div className="flex gap-2 items-center">
            <Avatar name={item.tokenDetail.tokenName?.[0]} color="primary" classNames={{ base: "!size-10" }} />
            <div>
              <div className="font-semibold">{item.tokenDetail.tokenName}</div>
              <div>
                <Snippet
                  size="sm"
                  hideSymbol
                  codeString={item.tokenDetail.l2Address || ""}
                  classNames={{
                    pre: "text-sm text-[#91A7B6]",
                    copyButton: "text-sm !min-w-4 !size-4",
                  }}
                >
                  {shortHex(item.tokenDetail.l2Address || "")}
                </Snippet>
              </div>
            </div>
          </div>
        );

      case "public_balance":
        if (balancesLoading) {
          return <Spinner size="sm" />;
        }
        if (!item.balance) {
          return <span className="text-[#91A7B6]">0</span>;
        }
        return (
          <div>
            <BalanceDisplay balance={item.balance.publicBalance} decimals={item.tokenDetail.tokenDecimals} />{" "}
            <span className="text-[#91A7B6]">{item.tokenDetail.tokenSymbol}</span>
          </div>
        );

      case "private_balance":
        if (balancesLoading) {
          return <Spinner size="sm" />;
        }
        if (!item.tokenDetail.l2Address) {
          return <span className="text-[#91A7B6]">-</span>;
        }
        return (
          <div className="flex items-center justify-end gap-2">
            <SimplePrivateBalance
              tokenAddress={item.tokenDetail.l2Address}
              decimals={item.tokenDetail.tokenDecimals}
            />
            <span className="text-[#91A7B6]">{item.tokenDetail.tokenSymbol}</span>
          </div>
        );

      case "actions":
        if (!item.tokenDetail.l2Address) {
          return null;
        }
        return (
          <div className="flex justify-end">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleRefresh(item.tokenDetail.l2Address!)}
              isLoading={refreshingTokens.has(item.tokenDetail.l2Address!)}
              className="text-[#91A7B6] hover:text-white"
            >
              <RefreshCwIcon size={16} />
            </Button>
          </div>
        );

      default:
        return "N/A";
    }
  };

  return (
    <Table
      aria-label="L2 Tokens"
      topContent={<TopContent />}
      sortDescriptor={sortDesc}
      onSortChange={setSortDesc}
      classNames={{
        base: "row-start-2 col-span-8",
        wrapper: "turnstile-card-border p-6",
        thead: "border-b",
        th: "text-[#ADC0D0] hover:!text-white text-medium font-medium bg-transparent",
        tbody: "tabular-nums *:h-20",
      }}
    >
      <TableHeader>
        <TableColumn key="tokenName" allowsSorting>
          Token name
        </TableColumn>
        <TableColumn key="public_balance" allowsSorting align="end">
          Public balance
        </TableColumn>
        <TableColumn key="private_balance" allowsSorting align="end">
          Private balance
        </TableColumn>
        <TableColumn key="actions" align="end">
          Actions
        </TableColumn>
      </TableHeader>
      <TableBody
        isLoading={tokensPending}
        loadingContent={<Spinner label="Loading tokens..." />}
        emptyContent="No tokens to display."
        items={sortedItems}
      >
        {(item) => (
          <TableRow key={item.tokenDetail.l2Address}>
            {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function TopContent() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <div className="flex gap-4 items-center">
      <div className="font-['Krona_One'] text-xl">Tokens & Balances</div>
      <div className="flex-1" />

      <NewL2TokenButton />

      <Button variant="bordered" color="primary" radius="full" startContent={<PlusIcon size={16} />} onPress={onOpen}>
        Add Token
      </Button>
      <AddTokenModal isOpen={isOpen} onClose={onClose} />
    </div>
  );
}

function AddTokenModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();

  const { balances } = useTokenBalances();
  const [tokenAddress, setTokenAddress] = useState("");

  const isValid = AztecAddress.isAddress(tokenAddress);

  async function handleSubmit() {
    if (!isValid) {
      toast.error("Invalid token address");
      return;
    }

    const isTokenContract = await isAztecTokenContract(AztecAddress.fromString(tokenAddress));
    if (!isTokenContract) {
      toast.error("Given address is not a token contract");
      return;
    }

    if (balances.has(tokenAddress)) {
      toast.error("Token already added");
      return;
    }

    addShieldingUserAddedTokens(tokenAddress);

    queryClient.invalidateQueries({ queryKey: ["all-token-balances"] });

    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="blur"
      placement="top"
      classNames={{ base: "turnstile-modal-border" }}
    >
      <ModalContent className="bg-[#3a617e]">
        <ModalHeader>Add Token</ModalHeader>

        <Divider />

        <ModalBody className="my-4 gap-6">
          <Input
            label="Token Address"
            variant="flat"
            value={tokenAddress}
            onValueChange={(value) => {
              setTokenAddress(value);
            }}
            endContent={isValid && <CheckIcon className="text-success" />}
            classNames={{
              inputWrapper:
                "bg-[var(--turnstile-input-color)] group-data-[hover=true]:bg-[var(--turnstile-input-hover-color)] group-data-[focus=true]:bg-[var(--turnstile-input-color)]",
            }}
          />

          <Button
            fullWidth
            size="lg"
            color={isValid ? "primary" : "default"}
            radius="full"
            isDisabled={!isValid}
            onPress={handleSubmit}
            className={isValid ? "" : "bg-[var(--turnstile-input-color)]"}
          >
            Submit
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
